import { z } from 'zod';
// Manual type for ETLPost:
// We do not use z.infer<typeof ETLPostSchema> here because ETLPostSchema is recursive (it references itself via comments and parent/children fields).
// TypeScript cannot resolve circular references with z.infer on recursive Zod schemas, so we define the type manually to avoid type inference issues and ensure type safety.
export const ETLPostSchema = z.object({
    id: z.string(),
    source: z.string(),
    source_id: z.string(),
    source_category_id: z.string().nullable().optional(),
    source_tags: z.array(z.string()),
    title: z.string(),
    description: z.string(),
    published_by_id: z.string(),
    published_by_name: z.string(),
    published_at: z.string().describe('Publication date in ISO format'),
    view_count: z.number(),
    like_count: z.number(),
    favorite_count: z.number().describe('DUPLICATED: Use like_count instead'),
    comment_count: z.number(),
    discussionDcRank: z.number().describe('Sum of discussion topic ranks'),
    categories: z
        .array(z.lazy(() => ETLCategorySchema))
        .nullable()
        .optional(),
    postAnalysis: z
        .lazy(() => ETLPostAnalysisSchema)
        .nullable()
        .optional(),
    discussionTopics: z
        .array(z.lazy(() => ETLDiscussionTopicSchema))
        .nullable()
        .optional(),
    targetAudience: z
        .array(z.lazy(() => ETLPostAudienceSchema))
        .nullable()
        .optional(),
});
// ETL Post Analysis entity schema (based on real TypeORM entity)
export const ETLPostAnalysisSchema = z.object({
    summary: z.string(),
    topKeywords: z.array(z.string()),
    unsaidWhys: z.array(z.string()).nullable().optional(),
    quadrant: z.string().nullable().optional(),
});
// ETL Discussion Topic entity schema (based on real TypeORM entity)
export const ETLDiscussionTopicSchema = z.object({
    topic: z.string(),
    description: z.string(),
    sentiment: z.string(),
    commentCount: z.number(),
    likeCount: z.number(),
    score: z.number().nullable().optional(),
    topicDcRank: z.number().nullable().optional(),
});
// ETL Category entity schema (based on real TypeORM entity)
export const ETLCategorySchema = z.object({
    name: z.string(),
    source: z.string(),
    url: z.string(),
});
// ETL Post Audience entity schema (based on real TypeORM entity)
export const ETLPostAudienceSchema = z.object({
    gender: z.string(),
    ageGroups: z.array(z.string()),
});
// Document query result schema
export const DocumentQueryResultSchema = z.object({
    fileName: z.string(),
    docId: z.string(),
    contentPage: z.number().nullable(),
    contentText: z.string().nullable(),
    score: z.number().nullable().optional(),
});
/**
 * Simplified unified source schema that reuses existing schemas
 * Represents both social media posts and internal documents with clean separation
 */
export const UnifiedSourceSchema = z.object({
    /** Unique key for this specific source instance. */
    key: z.string().describe('Unique key for this source instance'),
    /** Generic identifier that maps to post.id or doc.docId */
    id: z.string().describe('Generic identifier that maps to post.id or doc.docId'),
    url: z.string().nullable().describe('URL for the source instance').optional(),
    /** Social media post specific data - reusing existing ETL schema */
    post: ETLPostSchema.optional().describe('Social media post specific data'),
    /** Document-specific metadata - reusing existing document schema */
    document: DocumentQueryResultSchema.optional().describe('Document-specific metadata for knowledge-base documents'),
});
